import React, { useState, useEffect, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import { authFetch, apiUrl } from "../../../utils/api";

/**
 * BillingTab - Subscription management, cancel, payment methods
 *
 * Stupid simple: Big obvious buttons to manage/cancel via Stripe portal.
 * Overage settings live here too since they're billing-related.
 */
const BillingTab = () => {
  const { user } = useUser();
  const [billingStatus, setBillingStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = user?.publicMetadata?.role === "admin";
  const isVIP = user?.publicMetadata?.tier === "vip";
  const isUnlimited = isAdmin || isVIP;

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    setIsLoading(true);
    try {
      const [billingRes, creditsRes] = await Promise.all([
        authFetch(apiUrl("/api/billing/status")),
        authFetch(apiUrl("/api/usage/credits")),
      ]);
      const billingJson = await billingRes.json();
      const creditsJson = await creditsRes.json();
      const merged = { ...billingJson, ...creditsJson };
      setBillingStatus(merged);
    } catch (error) {
      console.error("Failed to load billing data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openBillingPortal = async () => {
    try {
      const response = await authFetch(apiUrl("/api/billing/portal"));
      const data = await response.json();
      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Failed to open billing portal:", error);
      alert("Failed to open billing portal. Please try again.");
    }
  };

  const getSubscriptionDisplay = () => {
    if (isUnlimited) {
      return { label: "Unlimited Access", color: "#bb86fc", badge: "VIP" };
    }
    const status = billingStatus?.status;
    switch (status) {
      case "trialing":
        return { label: "30-Day Trial", color: "var(--accent-primary)", badge: "Trial" };
      case "active":
        return { label: "Active", color: "var(--accent-success)", badge: "Active" };
      case "past_due":
        return { label: "Payment Past Due", color: "var(--accent-warning)", badge: "Past Due" };
      case "canceled":
        return { label: "Canceled", color: "var(--accent-danger)", badge: "Canceled" };
      default:
        return { label: "No Subscription", color: "var(--text-muted)", badge: "Inactive" };
    }
  };

  if (isLoading) {
    return (
      <div className="settings-section">
        <h3>💳 Billing</h3>
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text-secondary)" }}>
          Loading...
        </div>
      </div>
    );
  }

  const subDisplay = getSubscriptionDisplay();
  const hasSubscription = billingStatus?.status && billingStatus.status !== "none";

  return (
    <div className="settings-section">
      <h3>💳 Billing</h3>
      <p style={{ color: "var(--text-secondary)", marginBottom: "24px", fontSize: "14px" }}>
        Manage your subscription, payment method, and spending limits.
      </p>

      {/* Subscription Status */}
      <div
        className="hf-panel hf-reveal"
        style={{
          border: "1px solid var(--border-primary)",
          borderRadius: "12px",
          padding: "24px",
          background: "var(--bg-tertiary)",
          marginBottom: "24px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div>
            <h4 className="hf-count" style={{ margin: 0, color: "var(--text-primary)", fontSize: "16px" }}>
              {subDisplay.label}
            </h4>
            {billingStatus?.trialEndsAt && (
              <p style={{ margin: "4px 0 0 0", color: "var(--text-secondary)", fontSize: "13px" }}>
                Trial ends: {new Date(billingStatus.trialEndsAt).toLocaleDateString()}
              </p>
            )}
          </div>
          <div
            style={{
              padding: "6px 12px",
              background: subDisplay.color,
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: "500",
              color: "var(--text-on-accent)",
            }}
          >
            {subDisplay.badge}
          </div>
        </div>

        {/* Unlimited Access Info */}
        {isUnlimited && (
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "12px" }}>
            <span style={{ fontSize: "32px" }}>⚡</span>
            <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: "14px" }}>
              You have unlimited access. No billing limits apply.
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons - OBVIOUS and SIMPLE (admins/VIPs manage their Stripe too) */}
      {(hasSubscription || isUnlimited) && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "24px" }}>
          {/* Primary: Manage Subscription */}
          <button
            onClick={openBillingPortal}
            className="hf-lift hf-press hf-focus"
            style={{
              width: "100%",
              padding: "16px",
              background: "var(--accent-primary)",
              border: "none",
              borderRadius: "8px",
              color: "var(--text-on-accent)",
              fontSize: "15px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Manage Subscription
          </button>

          {/* Update Payment Method */}
          <button
            onClick={openBillingPortal}
            className="hf-press hf-focus"
            style={{
              width: "100%",
              padding: "14px",
              background: "var(--bg-tertiary)",
              border: "1px solid var(--border-primary)",
              borderRadius: "8px",
              color: "var(--text-primary)",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Update Payment Method
          </button>

          {/* View Invoices */}
          <button
            onClick={openBillingPortal}
            className="hf-press hf-focus"
            style={{
              width: "100%",
              padding: "14px",
              background: "var(--bg-tertiary)",
              border: "1px solid var(--border-primary)",
              borderRadius: "8px",
              color: "var(--text-primary)",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            View Invoices & Receipts
          </button>

          {/* Cancel - Red, clear */}
          <button
            onClick={openBillingPortal}
            className="hf-press hf-focus"
            style={{
              width: "100%",
              padding: "14px",
              background: "transparent",
              border: "1px solid var(--border-danger)",
              borderRadius: "8px",
              color: "var(--accent-danger)",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Cancel Subscription
          </button>
        </div>
      )}

      {/* Flat pricing: no usage metering, no overage — the old overage
          controls were removed 2026-07-07 (ARCHITECTURE_LOCKED.md chunk A4;
          server-side overage was already neutered to always-allow). */}

      {/* Support */}
      <div
        className="hf-panel"
        style={{
          padding: "16px",
          background: "var(--bg-tertiary)",
          borderRadius: "12px",
          border: "1px solid var(--border-primary)",
        }}
      >
        <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: "13px", lineHeight: "1.5" }}>
          Questions about billing?{" "}
          <a href="mailto:support@osoptimus.com" style={{ color: "var(--accent-primary)" }}>
            support@osoptimus.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default BillingTab;
